import {
  Magnet,
  PencilRuler,
  Redo2,
  ScanEye,
  SlidersHorizontal,
  Trash2,
  Undo2,
  Maximize2,
} from 'lucide-react';
import drawWallIcon from '../assets/design_tools/floorplan_to_image/ui/wall_icon.webp';
import doorIcon from '../assets/design_tools/floorplan_to_image/ui/door_icon.webp';
import windowIcon from '../assets/design_tools/floorplan_to_image/ui/window_icon.webp';
import FloorPlanSegmentedToggle from './FloorPlanSegmentedToggle.jsx';
import StyledRangeSlider from './StyledRangeSlider.jsx';

/** @typedef {import('../lib/floorPlanDesignerState.js').DesignerState} DesignerState */

const TOOL_ITEMS = [
  { id: 'wall', label: 'Wall', iconSrc: drawWallIcon },
  { id: 'door', label: 'Door', iconSrc: doorIcon },
  { id: 'window', label: 'Window', iconSrc: windowIcon },
];

/**
 * @param {{
 *   activeTool: string;
 *   mode?: 'design' | 'render';
 *   sidebarTopContent?: import('react').ReactNode;
 *   backgroundReferenceContent?: import('react').ReactNode;
 *   renderContent?: import('react').ReactNode;
 *   onActiveToolChange: (tool: string) => void;
 *   onFit?: () => void;
 *   onUndo?: () => void;
 *   onRedo?: () => void;
 *   modeToggle?: import('react').ReactNode;
 *   visibleHeightM?: number;
 *   maxVisibleHeightM?: number;
 *   onVisibleHeightChange?: (value: number) => void;
 *   snapToGrid: boolean;
 *   onSnapToGridChange: (v: boolean) => void;
 *   snapToPoints: boolean;
 *   onSnapToPointsChange: (v: boolean) => void;
 *   snapGridMeters: number;
 *   onSnapGridMetersChange: (v: number) => void;
 *   snapTolerancePx: number;
 *   onSnapToleranceChange: (v: number) => void;
 *   designerState: DesignerState;
 *   onClear: () => void;
 *   onExportJson: () => void;
 *   onCopyJson: () => void;
 *   previewError?: string;
 *   exportMessage?: string;
 *   debugJson?: string;
 * }} props
 */
function FloorPlanDesignerSidebar({
  mode = 'design',
  sidebarTopContent = null,
  backgroundReferenceContent = null,
  renderContent = null,
  activeTool,
  onActiveToolChange,
  onFit,
  onUndo,
  onRedo,
  modeToggle,
  visibleHeightM,
  maxVisibleHeightM = 2.9,
  onVisibleHeightChange,
  snapToGrid,
  onSnapToGridChange,
  snapGridMeters,
  onSnapGridMetersChange,
  snapTolerancePx,
  onSnapToleranceChange,
  onClear,
  previewError = '',
  exportMessage = '',
}) {
  const clampGridMeters = (value) => {
    const rounded = Math.round((Number(value) || 0.1) * 20) / 20;
    return Math.max(0.1, Math.min(0.5, rounded));
  };

  const clampTolerancePx = (value) => {
    const parsed = Number.parseInt(String(value), 10);
    if (!Number.isFinite(parsed)) return snapTolerancePx;
    return Math.max(4, Math.min(48, parsed));
  };

  const showVisibleHeight =
    modeToggle &&
    Number.isFinite(visibleHeightM) &&
    typeof onVisibleHeightChange === 'function';

  return (
    <aside className="floor-plan-designer-sidebar card" aria-label="Floor plan designer tools">
      {mode === 'render' ? (
        renderContent
      ) : (
        <>
          {sidebarTopContent ? (
            <section className="floor-plan-designer-sidebar-section floor-plan-designer-sidebar-section--production-upload">
              {sidebarTopContent}
            </section>
          ) : null}

          <section className="floor-plan-designer-sidebar-section floor-plan-designer-sidebar-section--compact">
            <h3 className="new-design-subheading">
              <SlidersHorizontal className="new-design-subheading-icon" size={18} strokeWidth={2} aria-hidden />
              View actions
            </h3>
            <div className="floor-plan-designer-tool-icons" role="toolbar" aria-label="View actions">
              <button
                type="button"
                className="floor-plan-designer-tool-icon-btn"
                onClick={onUndo}
                title="Undo"
                aria-label="Undo"
              >
                <Undo2 size={18} aria-hidden />
              </button>
              <button
                type="button"
                className="floor-plan-designer-tool-icon-btn"
                onClick={onRedo}
                title="Redo"
                aria-label="Redo"
                disabled={!onRedo}
              >
                <Redo2 size={18} aria-hidden />
              </button>
              <button
                type="button"
                className="floor-plan-designer-tool-icon-btn"
                onClick={onFit}
                title="Fit"
                aria-label="Fit"
              >
                <Maximize2 size={18} aria-hidden />
              </button>
            </div>
          </section>

          <section className="floor-plan-designer-sidebar-section floor-plan-designer-sidebar-section--compact">
            <h3 className="new-design-subheading">
              <PencilRuler className="new-design-subheading-icon" size={18} strokeWidth={2} aria-hidden />
              Drawing tools
            </h3>
            <div className="floor-plan-designer-tool-icons" role="toolbar" aria-label="Drawing tools">
              {TOOL_ITEMS.map(({ id, label, iconSrc }) => (
                <button
                  key={id}
                  type="button"
                  className={`floor-plan-designer-tool-icon-btn${activeTool === id ? ' floor-plan-designer-tool-icon-btn--active' : ''}${id === 'delete' && activeTool === 'delete' ? ' floor-plan-designer-tool-icon-btn--danger' : ''}`}
                  onClick={() => onActiveToolChange(id)}
                  title={label}
                  aria-label={label}
                  aria-pressed={activeTool === id}
                >
                  <img className="floor-plan-designer-tool-icon-img" src={iconSrc} alt="" aria-hidden="true" />
                </button>
              ))}
            </div>
          </section>

          {backgroundReferenceContent ? (
            <section className="floor-plan-designer-sidebar-section floor-plan-designer-sidebar-section--compact">
              {backgroundReferenceContent}
            </section>
          ) : null}

          {modeToggle ? (
            <section className="floor-plan-designer-sidebar-section floor-plan-designer-sidebar-section--compact">
              <h3 className="new-design-subheading">
                <ScanEye className="new-design-subheading-icon" size={18} strokeWidth={2} aria-hidden />
                View mode
              </h3>
              {modeToggle}
              {showVisibleHeight ? (
                <label className="floor-plan-visible-height-control">
                  <span className="floor-plan-visible-height-label">
                    Visible height
                    <span className="floor-plan-visible-height-value">{visibleHeightM.toFixed(2)} m</span>
                  </span>
                  <StyledRangeSlider
                    min={0}
                    max={maxVisibleHeightM}
                    step={0.05}
                    value={visibleHeightM}
                    onInput={(e) => onVisibleHeightChange(Number(e.currentTarget.value))}
                    aria-label="Visible height in meters"
                  />
                </label>
              ) : null}
            </section>
          ) : null}

          <section className="floor-plan-designer-sidebar-section floor-plan-designer-sidebar-section--compact">
            <h3 className="new-design-subheading">
              <Magnet className="new-design-subheading-icon" size={18} strokeWidth={2} aria-hidden />
              Snap settings
            </h3>
            <div className="floor-plan-designer-snap-row">
              <FloorPlanSegmentedToggle
                value={snapToGrid}
                options={[
                  { value: true, label: 'Snap On' },
                  { value: false, label: 'Snap Off' },
                ]}
                onChange={(next) => onSnapToGridChange(!!next)}
                ariaLabel="Snap to grid"
                className="floor-plan-designer-snap-toggle-group"
              />
              <label className="floor-plan-designer-grid-value" htmlFor="snap-grid-meters">
                <span className="sr-only">Grid size in meters</span>
                <input
                  id="snap-grid-meters"
                  type="number"
                  min="0.1"
                  max="0.5"
                  step="0.05"
                  className="new-2d-project-input"
                  value={snapGridMeters}
                  disabled={!snapToGrid}
                  onChange={(e) => onSnapGridMetersChange(clampGridMeters(e.target.value))}
                />
                <span>m</span>
              </label>
              <label className="floor-plan-designer-grid-value" htmlFor="snap-tolerance-px">
                <span className="sr-only">Snap tolerance in pixels</span>
                <input
                  id="snap-tolerance-px"
                  type="number"
                  min="4"
                  max="48"
                  step="1"
                  className="new-2d-project-input"
                  value={snapTolerancePx}
                  disabled={!snapToGrid}
                  onChange={(e) => onSnapToleranceChange(clampTolerancePx(e.target.value))}
                />
                <span>px</span>
              </label>
            </div>
          </section>

          <section className="floor-plan-designer-sidebar-section floor-plan-designer-sidebar-section--compact">
            <button
              type="button"
              className="btn btn-outline btn-sm floor-plan-designer-delete-all"
              onClick={onClear}
              title="Delete all"
              aria-label="Delete all"
            >
              <Trash2 size={16} aria-hidden />
              <span>Delete All</span>
            </button>
          </section>

          {previewError || exportMessage ? (
            <section className="floor-plan-designer-sidebar-section">
              {previewError ? (
                <p className="new-2d-project-error" role="alert">
                  {previewError}
                </p>
              ) : null}
              {exportMessage ? (
                <p className="new-2d-project-success" role="status">
                  {exportMessage}
                </p>
              ) : null}
            </section>
          ) : null}
        </>
      )}
    </aside>
  );
}

export default FloorPlanDesignerSidebar;
